"use client";

import { c as _c } from "react/compiler-runtime";
import type { Variants } from "framer-motion";
import type { HTMLAttributes } from "react";
import { forwardRef, useCallback, useImperativeHandle, useRef, useEffect, useState } from "react";
import { m, useAnimation } from "framer-motion";
import { cn } from "@/lib/utils";
export interface RocketIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}
interface RocketIconProps extends HTMLAttributes<HTMLDivElement> {
  size?: number;
  /** Auto-animate at interval (ms). Set to 0 to disable. */
  autoAnimateInterval?: number;
}

// Rocket body floating animation
const ROCKET_VARIANTS: Variants = {
  normal: {
    translateX: 0,
    translateY: 0,
    rotate: 0
  },
  animate: {
    translateX: [0, 2, 4, 2, 0],
    translateY: [0, -3, -5, -3, 0],
    rotate: [0, -2, -3, -2, 0],
    transition: {
      duration: 0.8,
      ease: "easeInOut"
    }
  }
};

// Flame flicker animation (staggered from rocket)
const FLAME_VARIANTS: Variants = {
  normal: {
    opacity: 1,
    scale: 1,
    pathLength: 1
  },
  animate: {
    opacity: [1, 0.7, 1, 0.8, 1],
    scale: [1, 1.15, 0.95, 1.1, 1],
    transition: {
      duration: 0.8,
      ease: "easeInOut"
    }
  }
};

// Window glow
const WINDOW_VARIANTS: Variants = {
  normal: {
    opacity: 1
  },
  animate: {
    opacity: [1, 0.6, 1],
    transition: {
      duration: 0.4,
      delay: 0.2
    }
  }
};
const RocketIcon = forwardRef<RocketIconHandle, RocketIconProps>((t0, ref) => {
  const $ = _c(47);
  let className;
  let onMouseEnter;
  let onMouseLeave;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      onMouseEnter,
      onMouseLeave,
      className,
      size: t1,
      autoAnimateInterval: t2,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = onMouseEnter;
    $[3] = onMouseLeave;
    $[4] = props;
    $[5] = t1;
    $[6] = t2;
  } else {
    className = $[1];
    onMouseEnter = $[2];
    onMouseLeave = $[3];
    props = $[4];
    t1 = $[5];
    t2 = $[6];
  }
  const size = t1 === undefined ? 24 : t1;
  const autoAnimateInterval = t2 === undefined ? 0 : t2;
  const controls = useAnimation();
  const isControlledRef = useRef(false);
  const containerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  let t3;
  if ($[7] !== controls) {
    t3 = () => {
      isControlledRef.current = true;
      return {
        startAnimation: () => controls.start("animate"),
        stopAnimation: () => controls.start("normal")
      };
    };
    $[7] = controls;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  useImperativeHandle(ref, t3);
  let t4;
  let t5;
  if ($[9] !== autoAnimateInterval) {
    t4 = () => {
      if (!containerRef.current || autoAnimateInterval === 0) {
        return;
      }
      const observer = new IntersectionObserver(t6 => {
        const [entry] = t6;
        return setIsVisible(entry.isIntersecting);
      }, {
        threshold: 0.1
      });
      observer.observe(containerRef.current);
      return () => observer.disconnect();
    };
    t5 = [autoAnimateInterval];
    $[9] = autoAnimateInterval;
    $[10] = t4;
    $[11] = t5;
  } else {
    t4 = $[10];
    t5 = $[11];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[12] !== autoAnimateInterval || $[13] !== controls || $[14] !== isVisible) {
    t6 = () => {
      if (autoAnimateInterval === 0 || !isVisible) {
        return;
      }
      const interval = setInterval(() => {
        controls.start("animate").then(() => {
          setTimeout(() => controls.start("normal"), 100);
        });
      }, autoAnimateInterval);
      return () => clearInterval(interval);
    };
    t7 = [autoAnimateInterval, isVisible, controls];
    $[12] = autoAnimateInterval;
    $[13] = controls;
    $[14] = isVisible;
    $[15] = t6;
    $[16] = t7;
  } else {
    t6 = $[15];
    t7 = $[16];
  }
  useEffect(t6, t7);
  let t8;
  if ($[17] !== controls || $[18] !== onMouseEnter) {
    t8 = e => {
      if (!isControlledRef.current) {
        controls.start("animate");
      }
      onMouseEnter?.(e);
    };
    $[17] = controls;
    $[18] = onMouseEnter;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const handleMouseEnter = t8;
  let t9;
  if ($[20] !== controls || $[21] !== onMouseLeave) {
    t9 = e_0 => {
      if (!isControlledRef.current) {
        controls.start("normal");
      }
      onMouseLeave?.(e_0);
    };
    $[20] = controls;
    $[21] = onMouseLeave;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const handleMouseLeave = t9;
  let t10;
  if ($[23] !== className) {
    t10 = cn("inline-flex items-center justify-center", className);
    $[23] = className;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== size) {
    t11 = {
      width: size,
      height: size
    };
    $[25] = size;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  let t14;
  let t15;
  let t16;
  if ($[28] !== controls) {
    t13 = <m.path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" variants={ROCKET_VARIANTS} initial="normal" animate={controls} />;
    t14 = <m.path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" variants={FLAME_VARIANTS} initial="normal" animate={controls} />;
    t15 = <m.path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" variants={FLAME_VARIANTS} initial="normal" animate={controls} />;
    t16 = <m.circle cx="14" cy="10" r="1" fill="currentColor" variants={WINDOW_VARIANTS} initial="normal" animate={controls} />;
    $[28] = controls;
    $[29] = t13;
    $[30] = t14;
    $[31] = t15;
    $[32] = t16;
  } else {
    t13 = $[29];
    t14 = $[30];
    t15 = $[31];
    t16 = $[32];
  }
  let t17;
  if ($[33] !== controls || $[34] !== size || $[35] !== t13 || $[36] !== t14 || $[37] !== t15 || $[38] !== t16) {
    t17 = <m.svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" variants={ROCKET_VARIANTS} initial="normal" animate={controls}>{t12}{t13}{t14}{t15}{t16}</m.svg>;
    $[33] = controls;
    $[34] = size;
    $[35] = t13;
    $[36] = t14;
    $[37] = t15;
    $[38] = t16;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== handleMouseEnter || $[41] !== handleMouseLeave || $[42] !== props || $[43] !== t10 || $[44] !== t11 || $[45] !== t17) {
    t18 = <div ref={containerRef} className={t10} style={t11} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} {...props}>{t17}</div>;
    $[40] = handleMouseEnter;
    $[41] = handleMouseLeave;
    $[42] = props;
    $[43] = t10;
    $[44] = t11;
    $[45] = t17;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  return t18;
});
RocketIcon.displayName = "RocketIcon";
export { RocketIcon };